/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/platform-admin/PlatformAdminLogin.tsx
 * PURPOSE: Platform admin authentication with NQH Brand Design System
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * CTO MANDATE: Production-grade authentication with multi-tenant security
 *
 * @business-context
 * - Domain: Multi-tenant Platform Administration, Vietnamese F&B
 * - Impact: Platform Critical - Authentication gateway
 * - Stakeholders: Platform Administrators, Nhat Quang Holding, IT Team
 *
 * @technical-architecture
 * - Layer: Presentation Layer, Page Components
 * - Pattern: React, CSS Modules, NQH Brand Design System
 * - Dependencies: React 18, React Router, TypeScript, CSS Modules
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/NQH-BRAND-DESIGN-SYSTEM.md
 * - API: docs/03-integrate/01-API-Specifications/
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { platformAdminApi } from '../../services/api/platform-admin/platformAdmin';
import { usePlatformAdminAuth } from '../../contexts/PlatformAdminContext';
import styles from './PlatformAdminLogin.module.css';

interface LoginCredentials {
  email_or_username: string;
  password: string;
}

export const PlatformAdminLogin: React.FC = () => {
  const [credentials, setCredentials] = useState<LoginCredentials>({
    email_or_username: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { setPlatformAdmin } = usePlatformAdminAuth();
  const navigate = useNavigate();

  // Update document title for platform admin
  useEffect(() => {
    document.title = 'NQH Platform Admin - Login';

    // Restore original title when component unmounts
    return () => {
      document.title = 'NQH-BOT Platform';
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await platformAdminApi.login(credentials);

      // Store platform admin authentication
      // FIX: Reducer reads snake_case keys (access_token, refresh_token, token_type)
      setPlatformAdmin({
        admin: response.admin,
        access_token: response.access_token,
        refresh_token: response.refresh_token,
        token_type: response.token_type,
      });

      // Redirect to platform admin dashboard
      navigate('/platform-admin/dashboard');
    } catch (err: any) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.icon}>🔧</div>
          <h1 className={styles.title}>
            NQH Quản trị Nền tảng
          </h1>
          <p className={styles.subtitle}>
            Quản lý nền tảng & Quản trị Tenant
          </p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className={styles.errorAlert}>
            {error}
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label className={styles.label}>
              Email hoặc Username
            </label>
            <input
              type="text"
              name="email"
              value={credentials.email_or_username}
              onChange={(e) => setCredentials(prev => ({
                ...prev,
                email_or_username: e.target.value
              }))}
              aria-label="Email or Username"
              className={styles.input}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
              Mật khẩu
            </label>
            <input
              type="password"
              name="password"
              value={credentials.password}
              onChange={(e) => setCredentials(prev => ({
                ...prev,
                password: e.target.value
              }))}
              aria-label="Password"
              className={styles.input}
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className={styles.submitButton}
          >
            {isLoading && <div className={styles.spinner} />}
            {isLoading ? 'Đang xác thực...' : '🔧 Đăng nhập Quản trị Nền tảng'}
          </button>
        </form>

        <div className={styles.footer}>
          <p className={styles.footerText}>
            Quản trị viên nền tảng có quyền truy cập quản lý tenant,<br />
            cấu hình hệ thống và vận hành toàn nền tảng.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PlatformAdminLogin;
